import React, { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container, Card, CardBody, CardHeader,
  Button, Row, Col, Input, Badge, Spinner,
  Table, InputGroup,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import { fetchStandList, setFilters, clearError, clearSuccess, deleteStand } from "../../../../slices/standSlice";
import KontrolluSilDugmesi from "../../../../Components/Common/KontrolluSilDugmesi";
import { useYetki, PERMISSION_KEYS } from "../../../../services/rbacService";
import MainContext from "../../../../Components/Contexts/MainContext";
import { useTranslation } from "react-i18next";
import i18n from "../../../../i18n";

const MenseiTag = ({ value }) => {
  if (!value) return null;
  const isYabanci = value === "Yabanci" || value === "Yabancı";
  return (
    <Badge color={isYabanci ? "danger" : "primary"} className="px-2 py-1" style={{ fontSize: "0.75rem" }}>
      {value}
    </Badge>
  );
};

const BorcBadges = ({ durumu = {} }) => {
  const { t } = useTranslation("moduller");
  const items = [
    { key: "suBorcu", label: t("standManagement.suBorcu") },
    { key: "havaBorcu", label: t("standManagement.havaBorcu") },
    { key: "elektrikBorcu", label: t("standManagement.elektrikBorcu") },
    { key: "alanBorcu", label: t("standManagement.alanBorcu") },
    { key: "projeBorcu", label: t("standManagement.projeBorcu") },
  ];
  return (
    <div className="d-flex flex-column gap-1">
      {items.map(({ key, label }) => (
        <Badge key={key} color={durumu[key] ? "danger" : "success"}
          className="text-nowrap px-2 py-1" style={{ fontSize: "0.7rem" }}>
          {label} {durumu[key] ? t("standManagement.var") : t("standManagement.yok")}
        </Badge>
      ))}
    </div>
  );
};

const StandManagementPage = () => {
  const { t: tx } = useTranslation("moduller");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selectedFuarId } = useContext(MainContext);
  const { list: standlar, loading, error, success, pagination, filters } = useSelector((state) => state.stand);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState("");
  const activeFuarId = selectedFuarId || 3;

  useEffect(() => { document.title = i18n.t("moduller:standManagement.standYonetimiFds"); }, []);

  useEffect(() => {
    dispatch(fetchStandList({
      fuarId: parseInt(activeFuarId),
      page: pagination.currentPage,
      size: pageSize,
      search: filters.search,
    }));
  }, [dispatch, activeFuarId, pagination.currentPage, pageSize, filters.search]);

  useEffect(() => {
    if (error || success) {
      const t = setTimeout(() => {
        if (error) dispatch(clearError());
        if (success) dispatch(clearSuccess());
      }, 3000);
      return () => clearTimeout(t);
    }
  }, [error, success, dispatch]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    dispatch(setFilters({ search: e.target.value }));
  };

  const handleGoDetail = (standId) => navigate(`/Management/Stands/detail/${standId}`);

  // #550: the delete is a SOFT delete behind STAND_REQUEST_MANAGE (403 otherwise), so the button is only drawn for someone who has it.
  // WAS: handleDelete → window.confirm + dispatch(deleteStand(standId)), and the service called DELETE /stand/{id}, which does not exist.
  const kaldirabilir = useYetki(PERMISSION_KEYS.STAND_REQUEST_MANAGE) === true;
  const silStand = async (standId) => {
    try {
      await dispatch(deleteStand(standId)).unwrap();
    } catch (e) {
      throw new Error(typeof e === "string" ? e : e?.message || "");
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("standManagement.standlar")} pageTitle={tx("standManagement.yonetim")} />
        <Row className="align-items-center mb-3">
          <Col><h5 className="mb-0 fw-semibold">{tx("standManagement.standlar")}</h5></Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button color="success" onClick={() => navigate(`/Management/Stands/${activeFuarId}`)}>
              {tx("standManagement.standOlustur")}
            </Button>
          </Col>
        </Row>
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center py-2">
            <div className="d-flex gap-2">
              <Button size="sm" color="dark" outline>{tx("standManagement.excel")}</Button>
              <Button size="sm" color="secondary" outline>{tx("standManagement.sutunGorunurlugu")}</Button>
            </div>
            <InputGroup size="sm" style={{ width: 220 }}>
              <span className="input-group-text"><i className="ri-search-line" /></span>
              <Input placeholder={tx("standManagement.ara")} value={search} onChange={handleSearchChange} />
            </InputGroup>
          </CardHeader>
          <div className="px-3 pt-2 d-flex align-items-center gap-2">
            <small>{tx("standManagement.sayfada")}</small>
            <Input type="select" bsSize="sm" style={{ width: 75 }} value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </Input>
            <small>{tx("standManagement.kayitGoster")}</small>
          </div>
          <CardBody className="pt-2">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">{tx("common:yukleniyor")}</p>
              </div>
            ) : (
              <div className="table-responsive">
                <Table bordered hover size="sm" className="align-middle mb-0" style={{ fontSize: "0.82rem" }}>
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>{tx("standManagement.stand")}</th>
                      <th>{tx("standManagement.salon")}</th>
                      <th>{tx("standManagement.firma")}</th>
                      <th>{tx("standManagement.mensei")}</th>
                      <th>{tx("standManagement.standTuru")}</th>
                      <th>{tx("standManagement.standYukseklik")}</th>
                      <th>{tx("standManagement.dekorator")}</th>
                      <th>{tx("standManagement.isIzinleri")}</th>
                      <th>{tx("standManagement.standDurumu")}</th>
                      <th>{tx("standManagement.elektrikTalebi")}</th>
                      <th>{tx("standManagement.suTalebi")}</th>
                      <th>{tx("standManagement.havaTalebi")}</th>
                      <th>{tx("standManagement.internetTalebi")}</th>
                      <th>#</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standlar.length === 0 ? (
                      <tr>
                        <td colSpan={15} className="text-center text-muted py-4">{tx("standManagement.kayitBulunamadi")}</td>
                      </tr>
                    ) : standlar.map((stand) => (
                      <tr key={stand.standId}>
                        <td>{stand.standId}</td>
                        <td>
                          <Button color="link" size="sm" className="p-0 text-primary fw-semibold"
                            onClick={() => handleGoDetail(stand.standId)}>
                            {stand.standName}
                          </Button>
                        </td>
                        <td>{stand.salonName}</td>
                        <td>
                          {stand.firmaName
                            ? <span className="text-primary" style={{ cursor: "pointer" }}>{stand.firmaName}</span>
                            : <span className="text-muted">-</span>}
                        </td>
                        <td><MenseiTag value={stand.mensei} /></td>
                        <td><small>{stand.standType || "-"}</small></td>
                        <td>{stand.standHeight || "-"}</td>
                        <td>
                          {stand.decoratorFirmaName
                            ? <span className="text-primary">{stand.decoratorFirmaName}</span>
                            : <span className="text-muted">-</span>}
                        </td>
                        <td>
                          <span className="badge bg-dark px-2">{stand.isIzinleriCount || "0/0"}</span>
                        </td>
                        <td><BorcBadges durumu={stand.standDurumu || {}} /></td>
                        <td>{stand.elektrikTalebi ? `${stand.elektrikTalebi} KW` : "-"}</td>
                        <td>{stand.suTalebi ? tx("standManagement.bar", { suTalebi: stand.suTalebi }) : "-"}</td>
                        <td>{stand.havaTalebi ? tx("standManagement.bar2", { havaTalebi: stand.havaTalebi }) : "-"}</td>
                        <td>
                          <Badge color={stand.internetTalebi === "Var" ? "success" : "secondary"}
                            style={{ fontSize: "0.72rem" }}>
                            {stand.internetTalebi || "-"}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <div className="d-flex gap-1">
                              <Button color="warning" size="sm" style={{ fontSize: "0.72rem" }}
                                onClick={() => handleGoDetail(stand.standId)}>
                                {tx("standManagement.kart")}
                              </Button>
                              <KontrolluSilDugmesi
                                key={stand.standId} tur="stand" ad={stand.standName} simgeSadece izin={kaldirabilir}
                                sil={() => silStand(stand.standId)}
                              />
                            </div>
                            <Button color="warning" size="sm"
                              style={{ fontSize: "0.72rem", backgroundColor: "#fd7e14", borderColor: "#fd7e14" }}
                              onClick={() => handleGoDetail(stand.standId)}>
                              {tx("standManagement.firmaEsle")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
            {!loading && standlar.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  {tx("standManagement.kayittanArasindakiKayitlar", { totalElements: pagination.totalElements, v: pagination.currentPage * pageSize + 1, v2: Math.min((pagination.currentPage + 1) * pageSize, pagination.totalElements) })}
                </small>
                <div className="d-flex gap-1">
                  <Button size="sm" outline disabled={pagination.currentPage === 0}>{tx("standManagement.onceki")}</Button>
                  <Button size="sm" color="primary">{pagination.currentPage + 1}</Button>
                  <Button size="sm" outline disabled={pagination.currentPage >= pagination.totalPages - 1}>{tx("standManagement.sonraki")}</Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default StandManagementPage;
